import {
  useInviteMobileUser,
  useProvisionMobileUsername,
  useResetMobileUserPassword,
  useMobileUsers,
  useReactivateMobileUser,
  useRemoveMobileUser,
  useSuspendMobileUser,
  useUpdateMobileUser,
  useResources,
  useLinkResourceMobileUser,
} from '@my/directory-api/hooks';
import type { MobileUserRoleValue, MobileUserSummary, Resource } from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  Loading,
  SelectInput,
  TextInput,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import {
  BadgeCheck,
  Ban,
  Check,
  Edit,
  KeyRound,
  Plus,
  RotateCcw,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface MobileUsersTabProps {
  /** Owning organisation id, resolved by the parent page from JWT claims. */
  organisationId: string;
}

const MOBILE_ROLES: MobileUserRoleValue[] = [
  'Driver',
  'Mechanic',
  'Operative',
  'Supervisor',
  'Other',
];

/**
 * How a new mobile user signs in. Both land on the same MobileUser record —
 * MobileNumberE164 and Username are each nullable — and Directory issues the
 * same token either way.
 *
 *  - `phone`    — invite by SMS, driver signs in with an OTP.
 *  - `username` — a username and password, for drivers who will not hand over
 *                 a mobile number.
 */
type InviteMethod = 'phone' | 'username';

interface EditorForm {
  displayName: string;
  mobileNumberE164: string;
  role: '' | MobileUserRoleValue;
  method: InviteMethod;
  /** As typed; Directory upper-cases it and requires it to be free. */
  username: string;
  /**
   * The driver record this login belongs to. Set here rather than afterwards so
   * the very first check they submit is attributable — a link nobody gets round
   * to making leaves those checks anonymous for good.
   */
  resourceId: string;
}

const EMPTY_FORM: EditorForm = {
  displayName: '',
  mobileNumberE164: '',
  role: '',
  method: 'phone',
  username: '',
  resourceId: '',
};

/**
 * Credentials handed back once by provisioning or a reset. The password is
 * never stored in readable form — Auth0 holds the hash, Directory holds
 * nothing — so this is the only chance to pass it on.
 */
interface IssuedCredentials {
  username: string;
  password: string;
}

const COLSPAN = 6;

/**
 * Mobile Users tab — admin management of the phone-authenticated identities
 * (drivers / operatives) that receive TMS jobs in the myDriver app. Distinct from the
 * Users tab (Auth0 web logins).
 *
 * Follows the house grid pattern: an inline editor row (`+ Invite` opens a
 * "new" row at the top; the row's Edit pencil / a double-click opens it in
 * place). The editor edits name + role, exposes Suspend / Reactivate, and
 * carries Delete on the left of its footer. All wired to `@my/directory-api`.
 */
export function MobileUsersTab({ organisationId }: MobileUsersTabProps) {
  const { t } = useTranslation('organisation');

  const usersQuery = useMobileUsers(organisationId, 'Tms');
  // Only drivers without a login already: linking a second identity to the same
  // driver is a re-issue, which belongs on the driver, not in an invite.
  const resourcesQuery = useResources(organisationId);
  const linkMutation = useLinkResourceMobileUser();
  const inviteMutation = useInviteMobileUser();
  const updateMutation = useUpdateMobileUser();
  const suspendMutation = useSuspendMobileUser();
  const reactivateMutation = useReactivateMobileUser();
  const removeMutation = useRemoveMobileUser();
  const provisionMutation = useProvisionMobileUsername();
  const resetPasswordMutation = useResetMobileUserPassword();

  /** Shown until dismissed — losing this means an admin reset. */
  const [issued, setIssued] = useState<IssuedCredentials | null>(null);

  // null = closed; 'new' = invite editor; a guid = editing that row.
  const [editorId, setEditorId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<EditorForm>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<MobileUserSummary | null>(null);

  const rows = usersQuery.data ?? [];
  const drivers = (resourcesQuery.data ?? []).filter((r) => r.type === 'Driver');
  const unlinkedDrivers = drivers.filter((r) => !r.mobileUserId);
  /** The driver currently pointing at this identity, if any. */
  const driverOf = (mobileUserId: string) =>
    drivers.find((r) => r.mobileUserId === mobileUserId) ?? null;

  /**
   * What the editor may choose from: every driver without a login, plus the one
   * already holding this identity — otherwise the current choice is not in the
   * list and the field reads as empty.
   */
  const driverChoices = (mobileUserId: string) => {
    const current = driverOf(mobileUserId);
    const all = current ? [...unlinkedDrivers, current] : unlinkedDrivers;
    return [...all].sort((a, b) =>
      (a.displayName || a.name).localeCompare(b.displayName || b.name),
    );
  };

  // What the editor was opened with, so save only calls the link endpoint when
  // the choice actually changed.
  const [originalResourceId, setOriginalResourceId] = useState('');
  const isBusy =
    inviteMutation.isPending ||
    updateMutation.isPending ||
    suspendMutation.isPending ||
    reactivateMutation.isPending ||
    removeMutation.isPending;

  const onError = (err: unknown, fallback: string) =>
    showToast({ type: 'error', message: err instanceof Error ? err.message : fallback });

  const openInvite = () => {
    setForm(EMPTY_FORM);
    setOriginalResourceId('');
    setSubmitted(false);
    setEditorId('new');
  };

  const openEdit = (row: MobileUserSummary) => {
    setForm({
      displayName: row.displayName,
      mobileNumberE164: row.mobileNumberE164 ?? '',
      // Editing never changes how an existing user signs in — they already
      // have whichever credential they were given.
      method: 'phone',
      username: '',
      resourceId: driverOf(row.id)?.id ?? '',
      role: row.role ?? '',
    });
    setOriginalResourceId(driverOf(row.id)?.id ?? '');
    setSubmitted(false);
    setEditorId(row.id);
  };

  const cancel = () => {
    setEditorId(null);
    setSubmitted(false);
  };

  /**
   * Move the link to whichever driver the editor now names. Two calls when it
   * moves between drivers: the endpoint is addressed by resource, and one
   * driver has to let go before the other can take it — a driver can hold only
   * one identity, so linking the new one first would be refused.
   */
  const applyDriverLinkChange = async (mobileUserId: string) => {
    if (form.resourceId === originalResourceId) return;

    if (originalResourceId) {
      await linkMutation.mutateAsync({
        resourceId: originalResourceId,
        organisationId,
        mobileUserId: null,
      });
    }
    if (form.resourceId) {
      await linkMutation.mutateAsync({
        resourceId: form.resourceId,
        organisationId,
        mobileUserId,
      });
    }
  };

  const handleSave = async () => {
    setSubmitted(true);
    const name = form.displayName.trim();
    if (!name) return;
    try {
      if (editorId === 'new' && form.method === 'username') {
        const res = await provisionMutation.mutateAsync({
          organisationId,
          displayName: name,
          username: form.username.trim() || null,
          role: form.role || null,
          appKey: 'Tms',
          appRole: 'User',
          resourceId: form.resourceId || null,
        });
        if (res.existingLogin) {
          // The username was already this person's login for another app;
          // Directory added this app to it and left their password alone.
          showToast({
            type: 'success',
            message: t('mobileUsers.addedToExistingLogin', { username: res.username }),
          });
        } else {
          // Surface before closing the editor — this is the only time the
          // password is readable anywhere.
          setIssued({ username: res.username, password: res.initialPassword ?? '' });
          showToast({ type: 'success', message: t('mobileUsers.provisioned') });
        }
      } else if (editorId === 'new') {
        if (!form.mobileNumberE164.trim()) return;
        const res = await inviteMutation.mutateAsync({
          organisationId,
          displayName: name,
          mobileNumberE164: form.mobileNumberE164.trim(),
          role: form.role || null,
          appKey: 'Tms',
          appRole: 'User',
          resourceId: form.resourceId || null,
        });
        showToast({
          type: 'success',
          // An invite with no SMS looks identical to one that sent, so say so.
          message: res.inviteSmsSent
            ? t('mobileUsers.invited')
            : t('mobileUsers.invitedNoSms'),
        });
      } else if (editorId) {
        await updateMutation.mutateAsync({
          id: editorId,
          body: { displayName: name, role: form.role || null },
        });
        await applyDriverLinkChange(editorId);
        showToast({ type: 'success', message: t('mobileUsers.updated', { name }) });
      }
      cancel();
    } catch (e) {
      onError(e, editorId === 'new' ? t('mobileUsers.inviteFailed') : t('mobileUsers.actionFailed'));
    }
  };

  const handleResetPassword = async (row: MobileUserSummary) => {
    try {
      const res = await resetPasswordMutation.mutateAsync({ id: row.id });
      setIssued({ username: res.username, password: res.newPassword });
    } catch (e) {
      onError(e, t('mobileUsers.actionFailed'));
    }
  };

  const handleSuspend = async (row: MobileUserSummary) => {
    try {
      await suspendMutation.mutateAsync(row.id);
      showToast({ type: 'success', message: t('mobileUsers.suspended', { name: row.displayName }) });
    } catch (e) {
      onError(e, t('mobileUsers.actionFailed'));
    }
  };

  const handleReactivate = async (row: MobileUserSummary) => {
    try {
      await reactivateMutation.mutateAsync(row.id);
      showToast({ type: 'success', message: t('mobileUsers.reactivated', { name: row.displayName }) });
    } catch (e) {
      onError(e, t('mobileUsers.actionFailed'));
    }
  };

  const confirmRemove = async () => {
    const row = pendingRemove;
    if (!row) return;
    try {
      await removeMutation.mutateAsync({ id: row.id, organisationId, app: 'Tms' });
      showToast({ type: 'success', message: t('mobileUsers.removed', { name: row.displayName }) });
      if (editorId === row.id) cancel();
    } catch (e) {
      onError(e, t('mobileUsers.actionFailed'));
    } finally {
      setPendingRemove(null);
    }
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4">
      <header className="mb-3 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">{t('mobileUsers.description')}</p>
        {usersQuery.isSuccess && editorId === null && (
          <button
            type="button"
            aria-label={t('mobileUsers.addAria')}
            onClick={openInvite}
            disabled={isBusy}
            className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('mobileUsers.add')}
          </button>
        )}
      </header>

      {/* One-time credentials. Deliberately not a toast: a toast disappears on
          its own, and this password cannot be recovered afterwards — only
          reset. It stays until explicitly dismissed. */}
      {issued && (
        <div className="rounded-sm border border-amber-300 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-900">
            {t('mobileUsers.credentials.title')}
          </p>
          <p className="mt-0.5 text-[11px] text-amber-800">
            {t('mobileUsers.credentials.warning')}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-4">
            <span className="text-xs text-slate-700">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                {t('mobileUsers.credentials.username')}
              </span>{' '}
              <code className="font-mono font-semibold">{issued.username}</code>
            </span>
            <span className="text-xs text-slate-700">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                {t('mobileUsers.credentials.password')}
              </span>{' '}
              <code className="font-mono font-semibold">{issued.password}</code>
            </span>
            <button
              type="button"
              onClick={() =>
                void navigator.clipboard?.writeText(
                  `${issued.username} / ${issued.password}`,
                )
              }
              className="rounded-sm border border-amber-300 px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
            >
              {t('mobileUsers.credentials.copy')}
            </button>
            <button
              type="button"
              onClick={() => setIssued(null)}
              className="ml-auto rounded-sm px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
            >
              {t('mobileUsers.credentials.dismiss')}
            </button>
          </div>
        </div>
      )}

      {usersQuery.isPending && <Loading label={t('mobileUsers.loading')} />}
      {usersQuery.isError && (
        <ErrorBanner title={t('mobileUsers.loadFailed')} message={usersQuery.error.message} />
      )}

      {usersQuery.isSuccess && rows.length === 0 && editorId !== 'new' && (
        <div className="flex items-center justify-center p-10 text-center">
          <div>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Smartphone className="h-6 w-6" />
            </div>
            <p className="text-sm font-medium text-slate-700">{t('mobileUsers.emptyTitle')}</p>
            <p className="mt-1 text-xs text-slate-500">{t('mobileUsers.emptyHint')}</p>
          </div>
        </div>
      )}

      {usersQuery.isSuccess && (rows.length > 0 || editorId === 'new') && (
        <div className="overflow-hidden rounded-md">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">{t('mobileUsers.table.name')}</th>
                  <th className="px-3 py-2 text-left font-semibold">{t('mobileUsers.table.mobile')}</th>
                  <th className="px-3 py-2 text-left font-semibold">{t('mobileUsers.table.role')}</th>
                  <th className="px-3 py-2 text-left font-semibold">{t('mobileUsers.table.status')}</th>
                  <th className="px-3 py-2 text-center font-semibold">{t('mobileUsers.table.devices')}</th>
                  <th className="px-3 py-2 text-right font-semibold">{t('mobileUsers.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {editorId === 'new' && (
                  <tr>
                    <td colSpan={COLSPAN} className="bg-slate-50 p-3">
                      <MobileUserEditor
                        isNew
                        draft={form}
                        linkableDrivers={unlinkedDrivers}
                        onChange={setForm}
                        submitted={submitted}
                        onSave={handleSave}
                        onCancel={cancel}
                        isBusy={isBusy}
                      />
                    </td>
                  </tr>
                )}

                {rows.map((row) =>
                  editorId === row.id ? (
                    <tr key={row.id}>
                      <td colSpan={COLSPAN} className="bg-slate-50 p-3">
                        <MobileUserEditor
                          draft={form}
                          linkableDrivers={driverChoices(row.id)}
                          onChange={setForm}
                          submitted={submitted}
                          status={row.status}
                          verified={row.identityVerified}
                          username={row.username}
                          onResetPassword={() => void handleResetPassword(row)}
                          onSave={handleSave}
                          onCancel={cancel}
                          onDelete={() => setPendingRemove(row)}
                          onSuspend={() => handleSuspend(row)}
                          onReactivate={() => handleReactivate(row)}
                          isBusy={isBusy}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={row.id}
                      onDoubleClick={() => openEdit(row)}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{row.displayName}</span>
                          {row.identityVerified && (
                            <BadgeCheck
                              className="h-3.5 w-3.5 text-emerald-600"
                              aria-label={t('mobileUsers.verified')}
                            />
                          )}
                        </div>
                      </td>
                      {/* One column, two credentials: a phone user shows a
                          number, a username user shows their login id. Showing
                          the raw field would leave username users blank. */}
                      <td className="px-3 py-1.5 font-mono text-[11px] text-slate-600">
                        {row.mobileNumberE164 ?? row.username ?? (
                          <span className="italic text-slate-300">—</span>
                        )}
                        {!row.mobileNumberE164 && row.username && (
                          <span className="ml-1 font-sans text-[10px] not-italic text-slate-400">
                            {t('mobileUsers.usernameTag')}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-slate-600">
                        {row.role
                          ? t(`mobileUsers.roles.${row.role}`, { defaultValue: row.role })
                          : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        <StatusBadge
                          status={row.status}
                          label={t(`mobileUsers.status.${row.status}`)}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-center text-slate-600">{row.deviceCount}</td>
                      <td className="px-3 py-1.5 text-right">
                        <button
                          type="button"
                          aria-label={t('mobileUsers.ariaEdit', { name: row.displayName })}
                          onClick={() => openEdit(row)}
                          disabled={isBusy}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingRemove !== null}
        tone="danger"
        title={t('mobileUsers.removeDialog.title')}
        message={
          pendingRemove
            ? t('mobileUsers.removeDialog.message', { name: pendingRemove.displayName })
            : null
        }
        confirmLabel={t('mobileUsers.removeDialog.confirm')}
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemove(null)}
        busy={removeMutation.isPending}
      />
    </section>
  );
}

// ── Inline editor ─────────────────────────────────────────────────────────────

interface MobileUserEditorProps {
  isNew?: boolean;
  draft: EditorForm;
  /** Drivers with no login yet — the ones this identity could be moved to. */
  linkableDrivers?: Resource[];
  onChange: (next: EditorForm) => void;
  submitted: boolean;
  /** Edit mode only — current platform status + verification. */
  status?: MobileUserSummary['status'];
  verified?: boolean;
  /** Set for username-based users; null for phone users, who sign in by OTP. */
  username?: string | null;
  /** Only offered for username users — phone users have no password to reset. */
  onResetPassword?: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  onSuspend?: () => void;
  onReactivate?: () => void;
  isBusy: boolean;
}

/**
 * Inline row editor for a mobile user. Invite (new) captures name + mobile +
 * role; edit changes name + role (the mobile number is the auth identity, so
 * it's read-only — remove + re-invite to change it) and exposes Suspend /
 * Reactivate. Footer: Delete on the left (edit only), Cancel + Save on the right.
 */
function MobileUserEditor({
  isNew = false,
  draft,
  linkableDrivers = [],
  onChange,
  submitted,
  status,
  verified,
  username,
  onResetPassword,
  onSave,
  onCancel,
  onDelete,
  onSuspend,
  onReactivate,
  isBusy,
}: MobileUserEditorProps) {
  const { t } = useTranslation('organisation');
  const set = <K extends keyof EditorForm>(key: K, value: EditorForm[K]) =>
    onChange({ ...draft, [key]: value });

  const roleOptions: SelectInputOption[] = [
    { value: '', label: t('mobileUsers.fields.roleNone') },
    ...MOBILE_ROLES.map((r) => ({ value: r, label: t(`mobileUsers.roles.${r}`, { defaultValue: r }) })),
  ];

  const driverOptions: SelectInputOption[] = [
    { value: '', label: t('mobileUsers.fields.driverNone') },
    ...linkableDrivers.map((d) => ({ value: d.id, label: d.displayName || d.name })),
  ];

  /**
   * Picking the driver fills the name and number from their record — the whole
   * point of choosing from a list rather than retyping, since a mistyped number
   * quietly creates a second identity for the same person. Only fills what is
   * still blank, so it never overwrites something already typed.
   */
  const pickDriver = (id: string) => {
    const driver = linkableDrivers.find((d) => d.id === id);
    // Prefill only on an invite. An existing user's name is their own — and on
    // a compliance record it is the name their past checks were signed with.
    if (!isNew) {
      onChange({ ...draft, resourceId: id });
      return;
    }
    onChange({
      ...draft,
      resourceId: id,
      displayName: draft.displayName.trim() || driver?.displayName || driver?.name || '',
      mobileNumberE164: draft.mobileNumberE164.trim() || driver?.mobileNumber || '',
    });
  };

  const nameInvalid = !draft.displayName.trim();
  const phoneInvalid = !draft.mobileNumberE164.trim();
  const canReactivate = status === 'Suspended' || status === 'Deleted';

  return (
    <div className="max-w-2xl space-y-3 rounded-md border border-slate-200 bg-white p-3">
      {/* Sign-in method. Some drivers will not give a mobile number,
          so username + password is a first-class route, not a fallback. */}
      {isNew && (
        <div className="flex flex-wrap gap-2 pb-1">
          <MethodButton
            active={draft.method === 'phone'}
            onClick={() => set('method', 'phone')}
            label={t('mobileUsers.method.phone')}
            hint={t('mobileUsers.method.phoneHint')}
          />
          <MethodButton
            active={draft.method === 'username'}
            onClick={() => set('method', 'username')}
            label={t('mobileUsers.method.username')}
            hint={t('mobileUsers.method.usernameHint')}
          />
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <TextInput
          label={t('mobileUsers.fields.displayName')}
          name="mobile-displayName"
          autoComplete="off"
          value={draft.displayName}
          onChange={(e) => set('displayName', e.target.value)}
          error={submitted && nameInvalid}
          errorMessage={t('mobileUsers.fields.displayNameRequired')}
        />
        {/* Phone or username — only a choice when creating. An existing user
            already has whichever they were given. */}
        {isNew && draft.method === 'username' ? (
          <TextInput
            label={t('mobileUsers.fields.username')}
            name="mobile-username"
            autoComplete="off"
            placeholder={t('mobileUsers.fields.usernamePlaceholder')}
            value={draft.username}
            onChange={(e) => set('username', e.target.value.toUpperCase())}
          />
        ) : (
          <TextInput
            label={t('mobileUsers.fields.mobileNumber')}
            name="mobile-number"
            autoComplete="off"
            placeholder={t('mobileUsers.fields.mobileNumberPlaceholder')}
            value={draft.mobileNumberE164}
            readOnly={!isNew}
            disabled={!isNew}
            onChange={(e) => set('mobileNumberE164', e.target.value)}
            error={submitted && isNew && draft.method === 'phone' && phoneInvalid}
            errorMessage={t('mobileUsers.fields.mobileNumberRequired')}
          />
        )}
        <SelectInput
          label={t('mobileUsers.fields.role')}
          name="mobile-role"
          value={draft.role}
          onChange={(e) => set('role', e.target.value as EditorForm['role'])}
          options={roleOptions}
        />
        {/* Shown when editing too: most drivers already have a login, so linking
            an existing one is the common case, not the exception. Optional on
            purpose — a mechanic or yard operative doing checks has no driver
            record, and forcing one would invent a driver that is not. */}
        <SelectInput
          label={t('mobileUsers.fields.driver')}
          name="mobile-driver"
          value={draft.resourceId}
          onChange={(e) => pickDriver(e.target.value)}
          options={driverOptions}
          helperText={t('mobileUsers.fields.driverHint')}
        />
      </div>

      {/* Status + suspend/reactivate (edit only) */}
      {!isNew && status && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {t('mobileUsers.table.status')}
          </span>
          <StatusBadge status={status} label={t(`mobileUsers.status.${status}`)} />
          {verified && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
              <BadgeCheck className="h-3.5 w-3.5" />
              {t('mobileUsers.verified')}
            </span>
          )}
          {/* Username users have no email, so there is no self-service reset —
              an admin issues a new password and hands it over. Phone users
              sign in with an OTP and have no password at all. */}
          {username && onResetPassword && (
            <button
              type="button"
              onClick={onResetPassword}
              disabled={isBusy}
              className="inline-flex items-center gap-1 rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {t('mobileUsers.resetPassword')}
            </button>
          )}
          {canReactivate ? (
            <button
              type="button"
              onClick={onReactivate}
              disabled={isBusy}
              className="ml-auto inline-flex items-center gap-1 rounded-sm border border-emerald-200 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {t('mobileUsers.reactivate')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onSuspend}
              disabled={isBusy}
              className="ml-auto inline-flex items-center gap-1 rounded-sm border border-amber-200 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            >
              <Ban className="h-3.5 w-3.5" />
              {t('mobileUsers.suspend')}
            </button>
          )}
        </div>
      )}

      {isNew && (
        <p className="text-[11px] text-slate-500">{t('mobileUsers.inviteHint')}</p>
      )}

      {/* Footer: Delete (left) · Cancel / Save (right) */}
      <div className="flex items-center justify-between gap-1 border-t border-slate-200 pt-3">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('mobileUsers.remove')}
              title={t('mobileUsers.remove')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('mobileUsers.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={isNew ? t('mobileUsers.sendInvite') : t('mobileUsers.ariaSave')}
            title={isNew ? t('mobileUsers.sendInvite') : t('mobileUsers.ariaSave')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_BADGE: Record<MobileUserSummary['status'], string> = {
  Invited: 'bg-amber-50 text-amber-700 ring-amber-200',
  Active: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Suspended: 'bg-red-50 text-red-700 ring-red-200',
  Deleted: 'bg-slate-100 text-slate-500 ring-slate-200',
};

function StatusBadge({ status, label }: { status: MobileUserSummary['status']; label: string }) {
  return (
    <span
      className={
        'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ' +
        STATUS_BADGE[status]
      }
    >
      {label}
    </span>
  );
}

/** One of the two sign-in routes. Labels are passed in — the typed-keys i18n
 *  augmentation only accepts literal keys at the call site. */
function MethodButton({
  active,
  onClick,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex-1 rounded-sm border px-3 py-2 text-left transition-colors ${
        active ? 'border-blue-400 bg-blue-50/60' : 'border-slate-300 bg-white hover:bg-slate-50'
      }`}
    >
      <span className={`block text-xs font-semibold ${active ? 'text-blue-700' : 'text-slate-900'}`}>
        {label}
      </span>
      <span className="mt-0.5 block text-[10px] text-slate-500">{hint}</span>
    </button>
  );
}
